/**
 * The tide report — the first open after a period closes (SPEC.md §9.1,
 * DESIGN_SYSTEM §4.1).
 *
 * One of the few places the myth voice is allowed (D-7): nothing is at stake,
 * the period is over, and the user's attention is already here. The headline is
 * the voice; every figure under it is plain. Arion on a dolphin — saved from
 * the sea by music — is the engraving.
 *
 * A full-screen dialog rather than a sheet: it is the whole of this open, and
 * "Start <Month>" is the only way on. Escape does the same.
 */

import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import { type Cents, absCents, formatMoney } from "@/domain/money";
import { Engraving } from "@/features/common/engraving";
import { cx } from "@/features/common/primitives";
import { type TideReport, periodName } from "@/domain/period_end";

function headline(report: TideReport): string {
  if (report.budget === null) return "The tide went out.";
  return report.held ? "The tide held." : "The water came over the mark.";
}

export function TideReportView({
  report,
  closedLabel,
  beforeLabel,
  currentLabel,
  currency,
  onStart,
}: {
  report: TideReport;
  closedLabel: string;
  beforeLabel: string;
  currentLabel: string;
  currency: string;
  onStart: () => void;
}): React.JSX.Element {
  const dialog = useRef<HTMLDivElement>(null);
  const money = (value: Cents): string => formatMoney(value, currency);

  useEffect(() => {
    dialog.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onStart();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onStart]);

  const current = periodName(currentLabel);
  const start = current === currentLabel && /\d/.test(current) ? "Start this period" : `Start ${current}`;

  // Portalled for the reason `Sheet` is: Home is an `isolate` stacking context,
  // and inside it the report would sit under the tab bar.
  return createPortal(
    <div
      ref={dialog}
      role="dialog"
      aria-modal="true"
      aria-label={`${closedLabel} closed`}
      tabIndex={-1}
      className="fixed inset-0 z-50 flex flex-col overflow-y-auto bg-bg pt-safe outline-none"
    >
      <Engraving name="arion" fade className="min-h-phi-6 w-full flex-1 [&_img]:h-full" />

      <div className="mx-auto flex w-full max-w-md flex-col px-phi-1 pt-phi-n2">
        <p className="text-phi-n1 font-semibold tracking-[0.16em] text-tx-2 uppercase">{closedLabel} closed</p>
        <h2 className="pt-phi-n4 text-phi-3 leading-tight font-bold tracking-tight text-balance">{headline(report)}</h2>
        <p className="pt-phi-n2 text-phi-0 text-tx-2">
          {report.budget === null || report.left === null ? (
            <>
              You spent <b className="fig text-tx">{money(report.spent)}</b>.
            </>
          ) : report.held ? (
            <>
              You spent <b className="fig text-tx">{money(report.spent)}</b> of{" "}
              <span className="fig">{money(report.budget)}</span>.{" "}
              <b className="fig text-tx">{money(report.left)}</b> stayed above the waterline.
            </>
          ) : (
            <>
              You spent <b className="fig text-tx">{money(report.spent)}</b> of{" "}
              <span className="fig">{money(report.budget)}</span>,{" "}
              <b className="fig text-over">{money(absCents(report.left))} over</b>.
            </>
          )}
        </p>
      </div>

      <dl className="mx-auto mt-phi-0 w-full max-w-md border-t border-line">
        {report.budgeted > 0 ? (
          <ReportRow label="Under the mark">
            {String(report.under)} of {String(report.budgeted)} categories
          </ReportRow>
        ) : null}
        {report.budgeted > 0 ? (
          <ReportRow label="Over" tone={report.over.length > 0 ? "over" : undefined}>
            {report.over.length === 0
              ? "Nothing"
              : report.over.map((o) => `${o.name} · ${money(o.by)} over`).join(", ")}
          </ReportRow>
        ) : null}
        {report.againstBefore === null ? null : (
          <ReportRow label={`Against ${periodName(beforeLabel)}`}>
            {report.againstBefore === 0
              ? "The same"
              : `${money(absCents(report.againstBefore))} ${report.againstBefore < 0 ? "less" : "more"}`}
          </ReportRow>
        )}
      </dl>

      <div className="mx-auto w-full max-w-md px-phi-1 pt-phi-1 pb-[max(env(safe-area-inset-bottom),var(--spacing-phi-1))]">
        <button
          type="button"
          onClick={onStart}
          className="flex min-h-phi-5 w-full items-center justify-center rounded-phi-n2 bg-acc px-phi-0 text-phi-0 font-bold text-on-acc"
        >
          {start}
        </button>
      </div>
    </div>,
    document.body,
  );
}

function ReportRow({
  label,
  tone,
  children,
}: {
  label: string;
  tone?: "over";
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-phi-0 border-b border-line px-phi-1 py-phi-n1">
      <dt className="text-phi-0">{label}</dt>
      <dd className={cx("fig ml-auto text-right text-phi-0", tone === "over" ? "font-semibold text-over" : "text-tx")}>
        {children}
      </dd>
    </div>
  );
}
